import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import langs from 'langs';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import SortableTree, { getVisibleNodeCount } from 'react-sortable-tree';
import { cloneDeep as _cloneDeep, filter as _filter, orderBy as _orderBy } from 'lodash';

import { addNotification } from 'actions/NotificationsActions';
import { deleterDispatchHelper } from 'actions/DeleterActions';
import { formStatusDispatchHelper } from 'actions/FormStatusActions';
import { getSystemTypeLabel, getSystemTypeIcon } from 'helpers/cms';
import { parseQs, stringifyQs } from 'helpers/http';

import Breadcrumb from 'components/layout/Breadcrumb';
import Loader from 'components/layout/Loader';
import SimplePrompt from 'components/layout/SimplePrompt';

import List from 'components/List';
import ListFilterLanguage from 'components/layout/list/filters/ListFilterLanguage';
import ListFilterWhiteLabelProject from 'components/layout/list/filters/ListFilterWhiteLabelProject';

import PageNew from 'components/modals/PageNew';
import PageDuplicate from 'components/modals/PageDuplicate';

import may from 'js/security';

import DocMeta from 'components/DocMeta';
import { generateMetaTags } from 'helpers/metaTags';

class Cms extends React.Component {
  constructor(props) {
    super(props);

    const query = parseQs(props.location.search);
    let replaceHistory = false;
    const addQueryParams = {};

    if (!query.whiteLabelProject) {
      replaceHistory = true;
      addQueryParams.whiteLabelProject = props.projectConfig.data.id;

      localStorage.setItem(
        `filterLabel-whiteLabelProject${props.projectConfig.data.id}`,
        props.projectConfig.data.name
      );
    }
    if (!query.language) {
      replaceHistory = true;
      addQueryParams.language = props.appIntl.locale;
    }

    if (replaceHistory) {
      props.history.replace({
        pathname: props.location.pathname,
        search: stringifyQs({ ...query, ...addQueryParams }),
      });
    }

    this.state = {
      showNew: false,
      showDuplicate: false,
      duplicatedPage: null,
      displayType: localStorage.getItem('pagesDisplayType') || 'list',
      pagesTree: [],
    };
  }

  showNew = () => {
    this.setState({ showNew: true });
  };

  closeNew = () => {
    this.setState({ showNew: false });
  };

  showDuplicate = (e, duplicatedPage) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ showDuplicate: true, duplicatedPage });
  };

  closeDuplicate = () => {
    this.setState({ showDuplicate: false });
  };

  handleRowClick = detailsUrl => {
    const { history } = this.props;
    history.push(detailsUrl);
  };

  getBreadcrumb = () => {
    const {
      intl: { messages },
      appIntl,
    } = this.props;

    return (
      <Breadcrumb
        crumbs={[{ link: appIntl.urlPrefix + 'account', name: 'Dashboard' }, { link: null, name: messages.cms_pages }]}
        className="p-t-0"
      />
    );
  };

  changeDisplayType = (e, displayType) => {
    e.stopPropagation();
    e.preventDefault();

    this.setState({ displayType });
    localStorage.setItem('pagesDisplayType', displayType);
  };

  handleRemoveClick = (e, pageId) => {
    e.preventDefault();
    e.stopPropagation();

    const { deletePage } = this.props;
    deletePage(pageId);
  };

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      appIntl,
      list,
      pageNewFormStatus,
      pageDuplicateFormStatus,
      doNotify,
    } = this.props;
    const { displayType } = this.state;

    // new page was saved
    if (prevProps.pageNewFormStatus.pending && !pageNewFormStatus.pending && !pageNewFormStatus.hasError) {
      this.closeNew();
      doNotify(1, messages.page_saved);
      this.handleRowClick(appIntl.urlPrefix + 'account/cms/' + pageNewFormStatus.response.id);
    }

    // a page was duplicated
    if (
      prevProps.pageDuplicateFormStatus.pending &&
      !pageDuplicateFormStatus.pending &&
      !pageDuplicateFormStatus.hasError
    ) {
      this.closeDuplicate();
      doNotify(1, messages.page_duplicated);
      this.handleRowClick(appIntl.urlPrefix + 'account/cms/' + pageDuplicateFormStatus.response.id);
    }

    if (displayType === 'tree' && prevProps.list.pending && !list.pending && !list.hasError) {
      const pagesTree = _orderBy(
        _cloneDeep(this.unflatten(list.data.list)),
        [o => !!o.system_type, o => !!o.children.length, o => o.name.toLowerCase()],
        ['desc', 'desc', 'asc']
      );
      this.setState({ pagesTree });
    }
  };

  getListContent = () => {
    const {
      intl: { messages },
      appIntl,
      list,
      user,
    } = this.props;

    const columns = [messages.name, messages.language, messages.actions];
    const tBody = [];
    for (let i = 0; i < list.data.list.length; i++) {
      const page = list.data.list[i];
      const { id, name, language, system_type: systemType } = page;

      const detailsUrl = appIntl.urlPrefix + 'account/cms/' + id;

      const languageLabel = language ? langs.where('1', language).local : '-';

      tBody.push(
        <tr key={i} className="clickable" onClick={() => this.handleRowClick(detailsUrl)}>
          <td className="v-align-middle">
            {!!systemType && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {messages.system_type}: {getSystemTypeLabel(systemType, messages)}
                  </Tooltip>
                }
              >
                <i className={'m-r-10 ' + getSystemTypeIcon(systemType)} />
              </OverlayTrigger>
            )}

            {name}
          </td>
          <td className="v-align-middle">
            <span title={language}>{languageLabel}</span>
          </td>
          <td className="v-align-middle text-right">
            <Link to={detailsUrl} className="btn-rounded m-r-10 btn btn-sm btn-default">
              <i className="fa fa-pencil-alt" /> {messages.edit}
            </Link>
            {may('ROLE_CMS_PAGES', user.roles) && (
              <a
                href="#duplicatePage"
                className="btn-rounded m-r-10 btn btn-sm btn-default"
                onClick={e => this.showDuplicate(e, page)}
              >
                <i className="fal fa-clone" /> {messages.duplicate}
              </a>
            )}
            {may('ROLE_CMS_MASTER', user.roles) && <SimplePrompt onSuccess={e => this.handleRemoveClick(e, id)} />}
          </td>
        </tr>
      );
    }

    return list.pending ? (
      <Loader />
    ) : (
      <Table responsive hover>
        <thead>
          <tr>
            {columns.map(column => {
              return (
                <th key={column} className={column === messages.actions ? 'text-right' : ''}>
                  {column}
                </th>
              );
            })}
          </tr>
        </thead>
        <ReactCSSTransitionGroup
          transitionName="tablerows"
          transitionEnterTimeout={500}
          transitionLeaveTimeout={1}
          component="tbody"
        >
          {tBody}
        </ReactCSSTransitionGroup>
      </Table>
    );
  };

  unflatten = (list, parentId = null) => {
    const {
      intl: { messages },
    } = this.props;

    return _filter(list, item => parentId === item.parent_id || (parentId === null && !item.parent_id)).map(item => {
      const { name, system_type: systemType } = item;

      return {
        ...item,
        title: (
          <div className="lh-30">
            {!!systemType && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {messages.system_type}: {getSystemTypeLabel(systemType, messages)}
                  </Tooltip>
                }
              >
                <i className={'m-r-10 m-l-10 hint-text ' + getSystemTypeIcon(systemType)} />
              </OverlayTrigger>
            )}{' '}
            {name}
          </div>
        ),
        collapsed: parentId !== true,
        children: this.unflatten(list, item.id),
      };
    });
  };

  handleTreeChange = pagesTree => {
    this.setState({ pagesTree });
  };

  handleTreeMove = ({ node, nextParentNode }) => {
    if (node.parent_id !== ((nextParentNode && nextParentNode.id) || null)) {
      const { editPage } = this.props;
      editPage(node.id, { parentId: (nextParentNode && nextParentNode.id) || null });
    }
  };

  getTreeContent = () => {
    const {
      intl: { messages },
      appIntl: { urlPrefix },
      user,
      list,
    } = this.props;
    const { pagesTree } = this.state;

    return list.pending ? (
      <Loader />
    ) : (
      <div className="panel">
        <div className="panel-body" style={{ height: getVisibleNodeCount({ treeData: pagesTree }) * 62 + 50 + 'px' }}>
          <SortableTree
            treeData={pagesTree}
            onChange={this.handleTreeChange}
            generateNodeProps={({ node }) => {
              const { id } = node;
              const detailsUrl = urlPrefix + 'account/cms/' + id;

              const buttons = [
                <>
                  <Link to={detailsUrl} className="btn-rounded m-r-10 btn btn-sm btn-default">
                    <i className="fa fa-pencil-alt" /> {messages.edit}
                  </Link>

                  {may('ROLE_CMS_PAGES', user.roles) && (
                    <a
                      href="#duplicatePage"
                      className="btn-rounded m-r-10 btn btn-sm btn-default"
                      onClick={e => this.showDuplicate(e, node)}
                      key="duplicate"
                    >
                      <i className="fal fa-clone" /> {messages.duplicate}
                    </a>
                  )}

                  {may('ROLE_CMS_MASTER', user.roles) && (
                    <SimplePrompt onSuccess={e => this.handleRemoveClick(e, id)} />
                  )}
                </>,
              ];

              return { buttons };
            }}
            onMoveNode={this.handleTreeMove}
            slideRegionSize={250}
            canDrag={({ node }) => !node.system_type}
            canDrop={({ nextParent }) => {
              return (nextParent && !nextParent.system_type) || !nextParent;
            }}
            canNodeHaveChildren={node => !node.system_type}
          />
        </div>
      </div>
    );
  };

  render() {
    const {
      intl: { messages },
      location,
      list,
    } = this.props;
    const { language, showNew, showDuplicate, duplicatedPage, displayType, whiteLabelProject } = this.state;

    const query = parseQs(location.search);

    const filters = [];
    filters.push(<ListFilterWhiteLabelProject key="white-label-project" multi={false} />);
    filters.push(<ListFilterLanguage key="language" />);

    let content = null;
    switch (displayType) {
      case 'list':
        content = this.getListContent();
        break;

      case 'tree':
        content = this.getTreeContent();
        break;
    }

    return (
      <DocMeta tags={generateMetaTags(messages.meta.pages)}>
        <List
          listName="pages"
          url="/api/pages.json"
          urlData={{ filters: { language: query.language, whiteLabelProject: query.whiteLabelProject } }}
          resPerPage={displayType === 'tree' ? 999 : 20}
          list={list}
          title={messages.cms_pages}
          iconClass="fa fa-desktop"
          showNew
          newText={messages.add_page}
          newAction={this.showNew}
          showSearch
          sortOptions={displayType === 'tree' ? [] : ['relevance,desc', 'name,asc', 'cdate,desc']}
          breadcrumb={this.getBreadcrumb()}
          displayTypes={['list', 'tree']}
          activeDisplayType={displayType}
          changeDisplayType={this.changeDisplayType}
          filters={filters}
        >
          {content}

          <div id="cms-new">
            <PageNew show={showNew} close={this.closeNew} />
          </div>
          <div id="cms-duplicate">
            <PageDuplicate show={showDuplicate} close={this.closeDuplicate} page={duplicatedPage} />
          </div>
        </List>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    list: state.list.pages,
    user: state.login.user,
    projectConfig: state.data.projectConfig,
    pageNewFormStatus: state.formStatus.pageNew,
    pageDuplicateFormStatus: state.formStatus.pageDuplicate,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    deletePage: pageId => {
      deleterDispatchHelper('page', `/api/pages/${pageId}.json`, dispatch);
    },
    editPage: (cmsId, args) => {
      formStatusDispatchHelper('pageEdit', `/api/pages/${cmsId}.json`, args, 'put', dispatch);
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Cms)
  )
);
